import { dualClockStamp } from "./clock";
import { fuelKindForType, lookupFuelPrice, type MoneyCatalogs } from "./catalogs";
import { findLogForTrip, hydrateArrivalReport, upsertLogList } from "./flightlog";
import {
  formatFuelAmount,
  fuelUsedToUsGal,
  resolveAircraftFuelUnit,
  type FuelUnit,
} from "./fuelUnits";
import { DATA_SOURCE_LABEL } from "./dataSource";
import { replaceTripFuelTxn } from "./ledger";
import { createSquawk } from "./maintenance";
import type {
  AircraftSpec,
  ArrivalReport,
  CompanyAccount,
  DeskRole,
  FlightLogEntry,
  Mission,
  RateTables,
  Squawk,
  SquawkSeverity,
} from "./types";

export type ArrivalApplySlices = {
  flightLogs: FlightLogEntry[];
  fleet: AircraftSpec[];
  missions?: Mission[];
  account: CompanyAccount;
  rates: RateTables;
  catalogs?: MoneyCatalogs;
  squawks: Squawk[];
  companyDefaultFuelUnit?: FuelUnit;
};

export type ArrivalReportInput = {
  flightLogId: string;
  notes?: string;
  maintenanceNotes?: string;
  nothingToReport?: boolean;
  /** Set to post/replace fuel. `null` clears a prior burn. Omit to keep existing. */
  fuelUsedAmount?: number | null;
  fuelUsedUnit?: FuelUnit;
  squawkTitle?: string;
  squawkSeverity?: SquawkSeverity;
  role?: DeskRole;
  /** Engine hours from the post-flight form. User edit for DOC accrual. */
  engineHours?: number;
};

export type ArrivalApplyResult = ArrivalApplySlices & {
  ok: boolean;
  error?: string;
  notice: string;
  log?: FlightLogEntry;
  squawk?: Squawk;
  fuelPosted: boolean;
};

function findLog(logs: FlightLogEntry[], id: string): FlightLogEntry | undefined {
  return logs.find((l) => l.id === id || l.tripId === id) ?? findLogForTrip(logs, id);
}

function parseFuelAmount(value: number | null | undefined): number | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (!Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value * 10) / 10;
}

export function arrivalFuelCostCad(input: {
  gallons: number;
  icao: string;
  icaoType?: string;
  catalogs?: MoneyCatalogs;
  rates: RateTables;
}): number {
  if (!(input.gallons > 0)) return 0;
  const kind = fuelKindForType(input.icaoType);
  const perGal = lookupFuelPrice(input.catalogs, input.icao, kind, input.rates.costs.fuelCadPerGal);
  return Math.round(input.gallons * perGal * 100) / 100;
}

export function applyArrivalReport(
  slices: ArrivalApplySlices,
  input: ArrivalReportInput,
  now = new Date(),
): ArrivalApplyResult {
  const log = findLog(slices.flightLogs, input.flightLogId);
  if (!log) {
    return {
      ...slices,
      ok: false,
      error: "Log not on file.",
      notice: "ARRIVAL  log not on file",
      fuelPosted: false,
    };
  }

  const spec =
    slices.fleet.find((a) => a.registration === log.registration) ??
    slices.fleet.find((a) => a.id === log.registration);
  const unit = input.fuelUsedUnit ?? resolveAircraftFuelUnit(spec, slices.companyDefaultFuelUnit);
  const prior = hydrateArrivalReport(log.arrival);
  const fuelAmount = parseFuelAmount(input.fuelUsedAmount);

  const notes =
    input.notes !== undefined ? input.notes.trim() : (prior?.notes ?? "");
  const maintenanceNotes =
    input.maintenanceNotes !== undefined ? input.maintenanceNotes.trim() : (prior?.maintenanceNotes ?? "");
  const nothingToReport = input.nothingToReport ?? prior?.nothingToReport ?? false;
  const squawkTitle = input.squawkTitle?.trim() ?? "";

  const hasFuel = typeof fuelAmount === "number";
  const clearingFuel = fuelAmount === null;
  const filedEngine =
    typeof input.engineHours === "number" && Number.isFinite(input.engineHours) && input.engineHours > 0
      ? Math.round(input.engineHours * 100) / 100
      : input.engineHours === undefined
        ? prior?.engineHours
        : undefined;
  if (!nothingToReport && !notes && !maintenanceNotes && !hasFuel && !clearingFuel && !squawkTitle && !filedEngine && !prior) {
    return {
      ...slices,
      ok: false,
      error: "Enter notes, fuel used, or mark nothing to report.",
      notice: "ARRIVAL  nothing entered",
      fuelPosted: false,
    };
  }

  const stamp = dualClockStamp(now);
  const mission = slices.missions?.find((m) => m.id === log.tripId);
  const fuelIcao = log.originIcao || mission?.originIcao || log.destIcao || "";
  const fuelKind = fuelKindForType(spec?.icaoType);

  let fuelUsedAmount = prior?.fuelUsedAmount;
  let fuelUsedUnit = prior?.fuelUsedUnit ?? unit;
  let fuelUsedGal = prior?.fuelUsedGal;
  let fuelCostCad = prior?.fuelCostCad;
  let account = slices.account;
  let fuelPosted = false;

  if (clearingFuel) {
    fuelUsedAmount = undefined;
    fuelUsedUnit = unit;
    fuelUsedGal = undefined;
    fuelCostCad = undefined;
  } else if (hasFuel) {
    fuelUsedAmount = fuelAmount;
    fuelUsedUnit = unit;
    fuelUsedGal = Math.round(fuelUsedToUsGal(fuelAmount, unit, fuelKind) * 10) / 10;
    fuelCostCad = arrivalFuelCostCad({
      gallons: fuelUsedToUsGal(fuelAmount, unit, fuelKind),
      icao: fuelIcao,
      icaoType: spec?.icaoType,
      catalogs: slices.catalogs,
      rates: slices.rates,
    });
    if (fuelCostCad > 0) {
      const memo = `Fuel ${formatFuelAmount(fuelAmount, unit)} (${fuelUsedGal.toFixed(1)} US gal, ${DATA_SOURCE_LABEL.user_override}) ${log.registration} ${fuelIcao}`;
      account = replaceTripFuelTxn(
        account,
        {
          missionId: log.tripId || undefined,
          aircraftId: spec?.id,
          createdAtUtc: now.toISOString(),
          kind: "fuel",
          amountCad: -fuelCostCad,
          memo,
        },
        now,
      );
      fuelPosted = true;
    }
  }

  let squawks = slices.squawks;
  let squawk: Squawk | undefined;
  const squawkIds = [...(prior?.squawkIds ?? [])];
  if (squawkTitle && spec) {
    squawk = createSquawk({
      aircraft: spec,
      severity: input.squawkSeverity ?? "info",
      title: squawkTitle,
      detail: maintenanceNotes,
      role: input.role ?? "pilot",
      now,
    });
    squawks = [squawk, ...squawks];
    squawkIds.push(squawk.id);
  }

  const arrival: ArrivalReport = {
    reportedAtUtc: prior?.reportedAtUtc || stamp.utc,
    reportedAtLocal: prior?.reportedAtLocal || stamp.local,
    notes: nothingToReport && !notes ? "Nothing to report." : notes,
    maintenanceNotes,
    nothingToReport,
    fuelUsedAmount,
    fuelUsedUnit,
    fuelUsedGal,
    fuelCostCad,
    engineHours: filedEngine,
    squawkIds: squawkIds.length ? squawkIds : undefined,
  };
  // Refresh updated stamp on each file / update.
  arrival.reportedAtUtc = stamp.utc;
  arrival.reportedAtLocal = stamp.local;

  const patched: FlightLogEntry = {
    ...log,
    arrival,
    notes: notes && !log.notes ? notes : log.notes,
    updatedAtUtc: stamp.utc,
    updatedAtLocal: stamp.local,
  };
  const flightLogs = upsertLogList(slices.flightLogs, patched);

  const bits = [`ARRIVAL  ${log.callsign} ${log.route}`];
  if (nothingToReport) bits.push("NTR");
  if (fuelUsedAmount != null && fuelUsedUnit) bits.push(`FUEL ${formatFuelAmount(fuelUsedAmount, fuelUsedUnit)}`);
  if (fuelPosted && fuelCostCad != null) bits.push(`LEDGER ${fuelCostCad.toFixed(2)}`);
  if (squawk) bits.push("SQUAWK");

  return {
    flightLogs,
    fleet: slices.fleet,
    missions: slices.missions,
    account,
    rates: slices.rates,
    catalogs: slices.catalogs,
    squawks,
    companyDefaultFuelUnit: slices.companyDefaultFuelUnit,
    ok: true,
    notice: bits.join("  ·  "),
    log: patched,
    squawk,
    fuelPosted,
  };
}
